import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Users, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useGroupSession,
  useGroupSessionParticipants,
  useCreateGroupSession,
  useUpdateGroupSession,
  useDeleteGroupSession,
  useAddParticipant,
  useRemoveParticipant,
  type GroupSessionStatus,
} from '@/hooks/useGroupSessions';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { Button } from '@/components/ui/Button';

export interface GroupSessionModalProps {
  clinicId: string;
  mode: 'create' | 'edit';
  sessionId?: string | null;
  /** Initial slot when creating (clicked time slot in the calendar). */
  initialStart?: Date | null;
  onClose: () => void;
}

function isoLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function fromInputValue(value: string): Date {
  return new Date(value);
}

export function GroupSessionModal(props: GroupSessionModalProps) {
  const { t } = useTranslation();
  const { clinicId, mode, sessionId, initialStart, onClose } = props;

  const { data: existing, isLoading: loadingExisting } = useGroupSession(
    mode === 'edit' ? sessionId : null,
  );
  const { data: participants } = useGroupSessionParticipants(mode === 'edit' ? sessionId : null);
  const { data: members } = useClinicMembers(clinicId);

  const create = useCreateGroupSession();
  const update = useUpdateGroupSession();
  const deleteSession = useDeleteGroupSession();
  const addParticipant = useAddParticipant();
  const removeParticipant = useRemoveParticipant();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clinicianId, setClinicianId] = useState<string | null>(null);
  const [startsAt, setStartsAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [maxParticipants, setMaxParticipants] = useState(20);
  const [minParticipants, setMinParticipants] = useState(1);
  const [pricePerSpotCents, setPricePerSpotCents] = useState(0);
  const [color, setColor] = useState('#6366F1');
  const [status, setStatus] = useState<GroupSessionStatus>('scheduled');
  const [notes, setNotes] = useState('');

  const [showAddParticipant, setShowAddParticipant] = useState(false);

  // Hydrate state when editing
  useEffect(() => {
    if (mode !== 'edit' || !existing) return;
    setName(existing.name);
    setDescription(existing.description ?? '');
    setClinicianId(existing.clinician_id);
    setStartsAt(isoLocal(new Date(existing.starts_at)));
    setDurationMinutes(existing.duration_minutes);
    setMaxParticipants(existing.max_participants);
    setMinParticipants(existing.min_participants);
    setPricePerSpotCents(existing.price_per_spot_cents);
    setColor(existing.color);
    setStatus(existing.status);
    setNotes(existing.notes ?? '');
  }, [mode, existing]);

  // Initial slot for create mode
  useEffect(() => {
    if (mode !== 'create') return;
    const start = initialStart ?? new Date();
    setStartsAt(isoLocal(start));
  }, [mode, initialStart]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('group_sessions.errors.name_required'));
      return;
    }

    const startsAtDate = fromInputValue(startsAt);
    const endsAtDate = new Date(startsAtDate.getTime() + durationMinutes * 60_000);

    try {
      if (mode === 'create') {
        await create.mutateAsync({
          clinic_id: clinicId,
          name: name.trim(),
          description: description.trim() || null,
          starts_at: startsAtDate.toISOString(),
          ends_at: endsAtDate.toISOString(),
          duration_minutes: durationMinutes,
          max_participants: maxParticipants,
          min_participants: minParticipants,
          price_per_spot_cents: pricePerSpotCents,
          color,
          clinician_id: clinicianId,
          notes: notes.trim() || null,
        });
        toast.success(t('group_sessions.created'));
      } else {
        await update.mutateAsync({
          id: sessionId!,
          name: name.trim(),
          description: description.trim() || null,
          starts_at: startsAtDate.toISOString(),
          ends_at: endsAtDate.toISOString(),
          duration_minutes: durationMinutes,
          max_participants: maxParticipants,
          min_participants: minParticipants,
          price_per_spot_cents: pricePerSpotCents,
          color,
          clinician_id: clinicianId,
          status,
          notes: notes.trim() || null,
        });
        toast.success(t('group_sessions.updated'));
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.save_failed'));
    }
  }

  async function handleDelete() {
    if (!sessionId) return;
    if (!confirm(t('group_sessions.confirm_delete'))) return;
    try {
      await deleteSession.mutateAsync(sessionId);
      toast.success(t('group_sessions.deleted'));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.delete_failed'));
    }
  }

  async function handleRemoveParticipant(participantId: string) {
    if (!sessionId) return;
    if (!confirm(t('group_sessions.confirm_remove_participant'))) return;
    try {
      await removeParticipant.mutateAsync({ participantId, sessionId });
      toast.success(t('group_sessions.participant_removed'));
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.remove_participant_failed'));
    }
  }

  const title =
    mode === 'create' ? t('group_sessions.create_title') : t('group_sessions.edit_title');
  const availableSpots = existing
    ? existing.max_participants - (existing as any).current_participants
    : maxParticipants;

  if (mode === 'edit' && loadingExisting) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="flex items-center gap-2 text-white">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
      <div className="relative w-full max-w-3xl rounded-lg bg-white shadow-xl dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('group_sessions.name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('group_sessions.description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Date/Time and Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('group_sessions.starts_at')} *
                </label>
                <input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  required
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('group_sessions.duration_minutes')} *
                </label>
                <input
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 60)}
                  min={15}
                  max={480}
                  required
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Capacity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('group_sessions.max_participants')} *
                </label>
                <input
                  type="number"
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(parseInt(e.target.value) || 1)}
                  min={1}
                  required
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('group_sessions.min_participants')} *
                </label>
                <input
                  type="number"
                  value={minParticipants}
                  onChange={(e) => setMinParticipants(parseInt(e.target.value) || 1)}
                  min={1}
                  max={maxParticipants}
                  required
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('group_sessions.price_per_spot')}
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  value={pricePerSpotCents / 100}
                  onChange={(e) => setPricePerSpotCents(Math.round((parseFloat(e.target.value) || 0) * 100))}
                  min={0}
                  step={0.01}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <span className="text-sm text-gray-500">DKK</span>
              </div>
            </div>

            {/* Clinician */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('group_sessions.clinician')}
              </label>
              <select
                value={clinicianId ?? ''}
                onChange={(e) => setClinicianId(e.target.value || null)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('group_sessions.no_clinician')}</option>
                {members?.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('group_sessions.color')}
              </label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1 h-10 w-20 rounded-md border border-gray-300 dark:border-gray-600"
              />
            </div>

            {/* Status (edit mode only) */}
            {mode === 'edit' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('group_sessions.status')}
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as GroupSessionStatus)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="scheduled">{t('group_sessions.status_scheduled')}</option>
                  <option value="completed">{t('group_sessions.status_completed')}</option>
                  <option value="cancelled">{t('group_sessions.status_cancelled')}</option>
                </select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('group_sessions.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Participants (edit mode only) */}
            {mode === 'edit' && existing && (
              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                    <Users className="h-4 w-4" />
                    {t('group_sessions.participants')} ({(existing as any).current_participants} /{' '}
                    {existing.max_participants})
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowAddParticipant(true)}
                    className="flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1 text-xs text-white hover:bg-indigo-700"
                  >
                    <UserPlus className="h-3 w-3" />
                    {t('group_sessions.add_participant')}
                  </button>
                </div>

                {participants && participants.length > 0 ? (
                  <div className="space-y-2">
                    {participants.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between rounded-md bg-gray-50 p-2 dark:bg-gray-700/50"
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {p.patient_name}
                          </div>
                          {p.patient_email && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {p.patient_email}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {p.spots} {p.spots === 1 ? 'spot' : 'spots'} •{' '}
                            {p.payment_amount_cents ? `${p.payment_amount_cents / 100} DKK` : 'No payment'}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveParticipant(p.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('group_sessions.no_participants')}
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            {mode === 'edit' && (
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={deleteSession.isPending}
              >
                {deleteSession.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {t('group_sessions.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {mode === 'create' ? t('group_sessions.create') : t('group_sessions.save')}
            </Button>
          </div>
        </div>
      </div>

      {/* Add Participant Modal */}
      {showAddParticipant && sessionId && (
        <AddParticipantModal
          clinicId={clinicId}
          sessionId={sessionId}
          availableSpots={availableSpots}
          pricePerSpotCents={pricePerSpotCents}
          onClose={() => setShowAddParticipant(false)}
          onAdd={addParticipant.mutateAsync}
        />
      )}
    </div>
  );
}

interface AddParticipantModalProps {
  clinicId: string;
  sessionId: string;
  availableSpots: number;
  pricePerSpotCents: number;
  onClose: () => void;
  onAdd: (input: any) => Promise<any>;
}

function AddParticipantModal({
  clinicId,
  sessionId,
  availableSpots,
  pricePerSpotCents,
  onClose,
  onAdd,
}: AddParticipantModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const pickerQuery = usePatientPicker(clinicId, searchQuery);
  const [selectedPatient, setSelectedPatient] = useState<PatientPickerItem | null>(null);
  const [spots, setSpots] = useState(1);
  const [isPending, setIsPending] = useState(false);

  async function handleAdd() {
    if (!selectedPatient) {
      toast.error(t('group_sessions.errors.select_patient'));
      return;
    }
    setIsPending(true);
    try {
      await onAdd({
        clinic_id: clinicId,
        group_session_id: sessionId,
        patient_id: selectedPatient.id,
        spots,
        payment_amount_cents: pricePerSpotCents * spots,
        payment_currency: 'DKK',
        payment_status: null,
      });
      toast.success(t('group_sessions.participant_added'));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.add_participant_failed'));
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('group_sessions.add_participant')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('group_sessions.select_patient')} *
            </label>
            <div className="relative mt-1">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('common.search')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              {pickerQuery.isLoading && (
                <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            {(pickerQuery.data ?? []).length > 0 && !selectedPatient && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
                {(pickerQuery.data ?? []).map((p: PatientPickerItem) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setSelectedPatient(p);
                      setSearchQuery('');
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {p.first_name} {p.last_name}
                    </div>
                    {p.email && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">{p.email}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedPatient && (
              <div className="mt-2 flex items-center justify-between rounded-md bg-green-50 p-2 dark:bg-green-900/20">
                <div className="text-sm font-medium text-green-800 dark:text-green-300">
                  {selectedPatient.first_name} {selectedPatient.last_name}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPatient(null)}
                  className="text-green-600 hover:text-green-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('group_sessions.spots')} *
            </label>
            <input
              type="number"
              value={spots}
              onChange={(e) => setSpots(Math.max(1, Math.min(availableSpots, parseInt(e.target.value) || 1)))}
              min={1}
              max={availableSpots}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <div className="mt-1 text-xs text-gray-500">
              {t('group_sessions.available_spots')}: {availableSpots}
            </div>
          </div>

          <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
            <div className="text-sm text-blue-800 dark:text-blue-300">
              {t('group_sessions.total_price')}: {((pricePerSpotCents * spots) / 100).toFixed(2)} DKK
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleAdd} disabled={!selectedPatient || isPending}>
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('group_sessions.add')}
          </Button>
        </div>
      </div>
    </div>
  );
}
